/**
 * Generic topological sort using DFS.
 *
 * @param items - Map of item IDs to item data
 * @param extractDeps - Function that returns dependency IDs for a given item.
 *   Only IDs present in `items` are followed; unknown IDs are silently ignored.
 * @returns Array of IDs in dependency order (dependencies before dependents)
 * @throws Error on circular dependencies
 */
export function topologicalSort<T>(items: Map<string, T>, extractDeps: (id: string, item: T) => string[]): string[] {
  const sorted: string[] = [];
  const visited = new Set<string>();
  const visiting = new Set<string>();

  const visit = (id: string) => {
    if (visited.has(id)) {
      return;
    }
    if (visiting.has(id)) {
      throw new Error(`Circular dependency detected: ${id}`);
    }

    const item = items.get(id);
    if (!item) {
      return;
    }

    visiting.add(id);

    const deps = extractDeps(id, item);
    for (const dep of deps) {
      if (items.has(dep)) {
        visit(dep);
      }
    }

    visiting.delete(id);
    visited.add(id);
    sorted.push(id);
  };

  for (const id of items.keys()) {
    visit(id);
  }

  return sorted;
}
